import { useEffect, useRef, useState } from 'react';
import styled, { withTheme, DefaultTheme } from 'styled-components';
import { Button } from 'antd';
import { useMediaQuery } from 'react-responsive';

import { IFlat } from '../../common/model/flat.model';
import useI18n from '../../common/hooks/useI18n';
import SearchMapMarker from './searchMapMarker';
import SearchMapPolygon from './searchMapPolygon';

type Props = {
  flats: IFlat[];
  focusedFlatIndex: number;
  highlightedPolygons: google.maps.LatLngLiteral[][];
  onMarkerClick: (flatIndex: number) => void;
  searchInMap: (bounds: google.maps.LatLngBounds) => void;
  theme: DefaultTheme;
};

const INITIAL_DELAY = 1000;
const MAX_ZOOM = 15;

const Container = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const MapDiv = styled.div`
  height: 100%;
  width: 100%;
  max-height: 100vh;
`;

const ShowHereButtonContainer = styled.div<{ $shown: boolean }>`
  position: absolute;
  top: 18px;
  @media ${(props) => props.theme.breakpoints.mdd} {
    top: 75px;
  }
  width: 100%;
  z-index: 1000;

  display: flex;
  justify-content: center;

  opacity: ${(props) => (props.$shown ? 1 : 0)};
  transition: opacity 0.2s ease;
`;

const SearchMap: React.FC<Props> = ({
  flats,
  focusedFlatIndex,
  highlightedPolygons,
  onMarkerClick,
  searchInMap,
  theme,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const i18n = useI18n();
  const isMdd = useMediaQuery({ query: theme.breakpoints.mdd });

  const initialDelayRef = useRef(isMdd ? INITIAL_DELAY : 0);

  const [map, setMap] = useState<google.maps.Map>();
  const [isMapVisible, setIsMapVisible] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [hasChangedLocked, setHasChangedLocked] = useState(true);
  const [fitBoundsCalled, setFitBoundsCalled] = useState(false);

  const onClickShowHere = () => {
    setHasChanged(false);
    setHasChangedLocked(true);
    setTimeout(() => {
      setHasChangedLocked(false);
    }, 1000);
    searchInMap(map.getBounds());
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsMapVisible(true);

      if (!map) {
        setTimeout(() => {
          const auxMap = new google.maps.Map(ref.current, {
            fullscreenControl: false,
            gestureHandling: 'greedy',
            mapTypeControl: false,
            streetViewControl: false,
            zoom: MAX_ZOOM + 1, // It will be adjusted after fitBounds
            zoomControl: !isMdd,
          });
          setMap(auxMap);
        }, 1000);
      }
    }, initialDelayRef.current);

    return () => clearTimeout(timer);
  }, [isMdd, map]);

  useEffect(() => {
    setTimeout(() => {
      setHasChangedLocked(false);
    }, initialDelayRef.current + 2000);
  }, [setHasChangedLocked]);

  useEffect(() => {
    if (map && flats.length > 0) {
      const mapBounds = new google.maps.LatLngBounds();
      for (const flat of flats) {
        mapBounds.extend(
          new google.maps.LatLng({
            lat: flat.approximateLatitude,
            lng: flat.approximateLongitude,
          })
        );
      }
      setFitBoundsCalled(true);
      map.fitBounds(mapBounds, 80);
    }
  }, [flats, map]);

  useEffect(() => {
    if (map) {
      ['bounds_changed'].forEach((eventName) =>
        google.maps.event.clearListeners(map, eventName)
      );

      const onBoundsChanged = () => {
        if (!hasChangedLocked) {
          setHasChanged(true);
        }
      };

      map.addListener('bounds_changed', () => onBoundsChanged());
    }
  }, [map, hasChanged, hasChangedLocked, setHasChanged, fitBoundsCalled]);

  useEffect(() => {
    if (map) {
      if (fitBoundsCalled) {
        setFitBoundsCalled(false);

        setTimeout(() => {
          if (map.getZoom() > MAX_ZOOM) {
            map.setZoom(MAX_ZOOM);
          }
        }, 1);
      }
    }
  }, [map, fitBoundsCalled]);

  if (!isMapVisible) {
    return null;
  }

  return (
    <Container>
      <MapDiv>
        <div ref={ref} style={{ height: '100%' }} />
        {flats.map((flat, i) => {
          const iconUrl =
            focusedFlatIndex == i
              ? '/images/map_marker_selected.svg'
              : '/images/map_marker.svg';
          return (
            <SearchMapMarker
              key={flat.id}
              flat={flat}
              isFocused={focusedFlatIndex === i}
              iconUrl={iconUrl}
              map={map}
              onMarkerClick={() => onMarkerClick(i)}
            />
          );
        })}
        {highlightedPolygons.map((p, i) => (
          <SearchMapPolygon key={i} highlightedCoordinates={p} map={map} />
        ))}
      </MapDiv>
      <ShowHereButtonContainer as="div" $shown={hasChanged}>
        <Button onClick={onClickShowHere}>
          {i18n.t('search.actions.showOnThisArea')}
        </Button>
      </ShowHereButtonContainer>
    </Container>
  );
};

export default withTheme(SearchMap);
